import React, { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Platform, PressableStateCallbackType, ViewStyle } from 'react-native'

import { BottomSheetFlashList } from '@gorhom/bottom-sheet'
import { FlashList, ListRenderItem } from '@shopify/flash-list'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { includes } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { sectionListHeaderMessages } from '@lyrahealth-inc/shared-app-logic'

import { Item, TypeAheadCustomOptionsConfig } from './TypeAheadCustomOptionsConfig'
import { AccessibilityRolesNative, IS_WEB } from '../../../constants'
import { MenuItemProps } from '../../../organisms'
import { HeadlineSize } from '../../../styles'
import { ThemeType, tID } from '../../../utils'
import { BodyText, Size as BodyTextSize } from '../../bodyText/BodyText'
import { Divider } from '../../divider/Divider'
import { Headline } from '../../headline/Headline'
import { CheckMarkIcon } from '../../icons'

export enum MENU_SELECTED_ITEM_STYLE {
  CHECKMARK = 'checkmark',
  LEFT_BORDER = 'leftBorder',
}

export type GetMenuItemStyle = (args: {
  hovered?: boolean
  pressed?: boolean
  focused?: boolean
  selected?: boolean
  text?: string
}) => ViewStyle

export interface DropdownMenuItemProps<T> {
  onPress: (text: string, customSelectedItem?: T) => void
  option: string
  highlightValue?: string
  selected?: boolean
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  customOption?: T
  value: string[]
  item?: Item
  selectedItemStyle?: MENU_SELECTED_ITEM_STYLE | null
  getMenuItemStyle?: GetMenuItemStyle
  disabled?: boolean
  idPrefix?: string
  index: number
  /** Use simple header style (smaller text, secondary color) */
  simpleHeaderStyle?: boolean
  /** Use active/teal color for menu item text */
  useActiveTextColor?: boolean
}

export interface DropdownMenuProps<T> {
  onOptionPress: (text: string, customSelectedItem?: T) => void
  options: string[]
  highlightValue?: string
  optionSelected?: number
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  /** Subset of the `options` list in `customOptionsConfig` (filtered by search query) */
  customOptions?: T[]
  /** List of all selected options */
  value: string[]
  initialScrollIndex?: number
  selectedItemStyle?: MENU_SELECTED_ITEM_STYLE | null
  showsVerticalScrollIndicator?: boolean
  scrollEnabled?: boolean
  getMenuItemStyle?: GetMenuItemStyle
  listFooterComponent?: React.ReactElement
  listFooterComponentStyle?: ViewStyle
  scrollEnabledWithinBottomSheet?: boolean
  idPrefix?: string
  /** Use simple header style (smaller text, secondary color) */
  simpleHeaderStyle?: boolean
  /** Use active/teal color for menu item text */
  useActiveTextColor?: boolean
}

const MenuItem = styled.Pressable({
  flexDirection: 'row',
  borderRadius: '4px',
})

const DisplayTextContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const CheckMarkIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingLeft: Platform.OS === 'web' ? theme.spacing['16px'] : '36px',
}))

const SelectedItemLeftBorder = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  borderTopRightRadius: theme.spacing['16px'],
  borderBottomRightRadius: theme.spacing['16px'],
  backgroundColor: theme.colors.backgroundActive,
  width: '4px',
  top: '3px',
  bottom: '3px',
  left: 0,
}))

const SectionHeader = styled(BodyText)({
  flexDirection: 'row',
  padding: '12px 16px 8px 16px',
})

const SectionHeaderEyebrow = styled(Headline)(({ theme }) => ({
  padding: `${theme.spacing['8px']} ${theme.spacing['16px']}`,
  color: theme.colors.textSecondary,
}))

const SimpleHeader = styled(BodyText)<{ theme: ThemeType; isFirst?: boolean }>(({ theme, isFirst }) => ({
  padding: '12px 16px 8px 16px',
  color: theme.colors.textSecondary,
  ...(isFirst ? {} : { borderTopWidth: 1, borderTopColor: theme.colors.borderDefault }),
}))

const DropdownMenuItemNotMemoized = <T,>({
  onPress,
  option,
  highlightValue = '',
  selected,
  customOptionsConfig,
  customOption,
  value,
  item,
  selectedItemStyle,
  disabled = false,
  getMenuItemStyle,
  idPrefix,
  index,
  simpleHeaderStyle = false,
  useActiveTextColor = false,
}: DropdownMenuItemProps<T>): JSX.Element => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const menuItemPadding = { paddingVertical: 8, paddingHorizontal: 16 }
  const { menuItem_header, menuItem_divider, menuItem_section, useEyebrow } = item || {}
  if (menuItem_header) {
    // Check if there's a predefined i18n message for this header, otherwise use the raw text
    const headerMessage = sectionListHeaderMessages[option as keyof typeof sectionListHeaderMessages]
    const headerText = headerMessage ? formatMessage(headerMessage) : option
    if (simpleHeaderStyle) {
      return (
        <SimpleHeader
          text={headerText}
          size={BodyTextSize.SMALL}
          isFirst={index === 0}
          testID={tID(`typeAhead-menu-section-header-${option}`)}
        />
      )
    }
    if (useEyebrow) {
      return <SectionHeaderEyebrow size={HeadlineSize.EYEBROW} text={headerText} />
    }
    return (
      <SectionHeader
        text={headerText}
        size={BodyTextSize.LARGE}
        testID={tID(`typeAhead-menu-section-header-${option}`)}
      />
    )
  } else if (menuItem_divider) {
    return <Divider height={1} style={{ marginTop: 12, marginBottom: 12 }} />
  }

  const stringMatches = match(option, highlightValue, { insideWords: true })
  const parsedText = parse(option, stringMatches)
  const valueToMatch = customOption ? customOptionsConfig?.getValueForItem(customOption) : option
  const isSelectedOption = value?.length ? includes(value, valueToMatch) : value && value[0] === valueToMatch
  const accessibilityLabel = formatMessage(
    {
      defaultMessage: 'List item {option}, {isSectionItem, select, true {section {sectionName}} other {}}',
      description: 'Accessibility label for an item in a dropdown list',
    },
    {
      option,
      sectionName: menuItem_section,
      isSectionItem: !!menuItem_section,
    },
  )
  const defaultTextColor = useActiveTextColor ? colors.textActive : colors.textPrimary
  const getHighlightedText = (extraProps?: any) => {
    return parsedText.map(({ text, highlight }) => (
      <BodyText
        key={`${text}-${highlight}`}
        text={text}
        size={BodyTextSize.DEFAULT}
        underline={highlight}
        bold={highlight}
        color={defaultTextColor}
        {...extraProps}
        accessibilityLabel={accessibilityLabel}
      />
    ))
  }
  const displayText = isSelectedOption ? (
    <DisplayTextContainer>
      {getHighlightedText({ color: colors.textActive })}
      {selectedItemStyle === MENU_SELECTED_ITEM_STYLE.CHECKMARK && (
        <CheckMarkIconContainer>
          <CheckMarkIcon />
        </CheckMarkIconContainer>
      )}
    </DisplayTextContainer>
  ) : (
    <DisplayTextContainer>{getHighlightedText()}</DisplayTextContainer>
  )

  const menuItemContent =
    customOption && customOptionsConfig?.menuItemRenderer
      ? customOptionsConfig?.menuItemRenderer(customOption, displayText, isSelectedOption)
      : displayText

  return (
    <MenuItem
      accessible
      focusable
      disabled={disabled}
      onPress={() => onPress(option, customOption)}
      onKeyDown={(event) => {
        if (event?.key === 'Enter') {
          onPress(option, customOption)
        }
      }}
      testID={tID((customOption && customOptionsConfig?.getOptionTestId(customOption)) || 'typeAhead-menu-item')}
      style={({ hovered, pressed, focused }: PressableStateCallbackType) => {
        const baseStyles = [menuItemPadding]
        if (disabled) {
          return [{ opacity: 0.5 }, ...baseStyles]
        }
        return [
          (hovered || selected || isSelectedOption) && { backgroundColor: colors.typeaheadItemBackgroundHover },
          pressed && { backgroundColor: colors.tertiaryButtonBackgroundPressed },
          focused && {
            outlineStyle: 'solid',
            outlineColor: colors.borderFocus,
            outlineWidth: 2,
            outlineOffset: -2,
          },
          ...baseStyles,
          getMenuItemStyle && getMenuItemStyle({ hovered, pressed, focused, selected: isSelectedOption, text: option }),
        ]
      }}
      accessibilityLabel={accessibilityLabel}
      {...(IS_WEB
        ? {
            role: 'option',
            'aria-label': option,
            'aria-selected': isSelectedOption,
            tabIndex: 0,
            ...(idPrefix ? { id: `${idPrefix}-option-${index}` } : {}),
          }
        : {
            accessibilityRole: AccessibilityRolesNative.BUTTON,
            accessibilityState: { disabled, selected: isSelectedOption },
          })}
    >
      {isSelectedOption && selectedItemStyle === MENU_SELECTED_ITEM_STYLE.LEFT_BORDER && <SelectedItemLeftBorder />}
      {menuItemContent}
    </MenuItem>
  )
}
export const DropdownMenuItem = React.memo(DropdownMenuItemNotMemoized)

/**
 * A dropdown component that is used to display a list of items
 * - Currently used in `TypeAhead`, `DropdownButton`, and `Menu`
 *
 * todo: move component so that it is separate from the typeahead
 */
export const DropdownMenu = <T,>({
  options,
  onOptionPress,
  highlightValue,
  optionSelected,
  customOptionsConfig,
  customOptions,
  value,
  initialScrollIndex,
  selectedItemStyle = MENU_SELECTED_ITEM_STYLE.CHECKMARK,
  getMenuItemStyle,
  showsVerticalScrollIndicator,
  scrollEnabled,
  listFooterComponent,
  listFooterComponentStyle,
  // Use mobile-specific container to render items which fixes bug where container not scrollable
  scrollEnabledWithinBottomSheet = false,
  idPrefix,
  simpleHeaderStyle = false,
  useActiveTextColor = false,
}: DropdownMenuProps<T>): JSX.Element => {
  const ITEM_HEIGHT = 40
  const ref = useRef<FlashList<string> | null>(null)
  // follow highlighted item
  useEffect(() => {
    if (ref.current) {
      ;(ref.current as FlashList<string>).scrollToIndex({ animated: true, index: optionSelected || 0 })
    }
  }, [optionSelected])

  const renderItem: ListRenderItem<string> = ({ item, index, extraData: { highlightValue, value } }) => {
    return (
      <DropdownMenuItem
        option={item}
        onPress={onOptionPress}
        highlightValue={highlightValue}
        selected={optionSelected === index}
        value={value}
        selectedItemStyle={selectedItemStyle}
        getMenuItemStyle={getMenuItemStyle}
        key={`dropdownMenuItem-${index}`}
        idPrefix={idPrefix}
        index={index}
        simpleHeaderStyle={simpleHeaderStyle}
        useActiveTextColor={useActiveTextColor}
      />
    )
  }
  const renderItemCustom: ListRenderItem<T> = ({ item, index, extraData: { highlightValue, value } }) => {
    return (
      <DropdownMenuItem
        option={customOptionsConfig?.getDisplayString(item) || ''}
        onPress={onOptionPress}
        highlightValue={highlightValue}
        selected={optionSelected === index}
        customOptionsConfig={customOptionsConfig}
        customOption={item}
        value={value}
        item={item as Item}
        selectedItemStyle={selectedItemStyle}
        getMenuItemStyle={getMenuItemStyle}
        disabled={(item as MenuItemProps).disabled}
        idPrefix={idPrefix}
        index={index}
        simpleHeaderStyle={simpleHeaderStyle}
        useActiveTextColor={useActiveTextColor}
      />
    )
  }
  const keyExtractor = (option: string) => option
  const keyExtractorCustom = (option: T) => customOptionsConfig?.getValueForItem(option) || ''
  const BottomSheetContainer = useMemo(() => {
    return scrollEnabledWithinBottomSheet ? BottomSheetFlashList : FlashList
  }, [scrollEnabledWithinBottomSheet])

  return (
    <>
      {/* Default string option items */}
      {!customOptionsConfig && (
        <BottomSheetContainer
          data={options}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          estimatedItemSize={ITEM_HEIGHT}
          initialScrollIndex={initialScrollIndex}
          /* FlashList will not re-render the list if a prop in the renderItem function changes
             so to ensure the highlighting of text disappears after a user backspaces all characters, include that in extraData.
             Issue here: https://github.com/Shopify/flash-list/issues/699 */
          extraData={{ highlightValue, value }}
          // @ts-expect-error MutableRefObject<FlashList<string> | null> is not assignable to Ref<FC<{}>> | undefined
          ref={ref}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          scrollEnabled={scrollEnabled}
          ListFooterComponent={listFooterComponent}
          ListFooterComponentStyle={listFooterComponentStyle}
        />
      )}

      {/* Custom option items, rendered using `TypeAheadCustomOptionsConfig menuItemRenderer()` */}
      {customOptionsConfig && customOptions && (
        <FlashList
          data={customOptions}
          renderItem={renderItemCustom}
          keyExtractor={keyExtractorCustom}
          estimatedItemSize={ITEM_HEIGHT}
          drawDistance={ITEM_HEIGHT} // render ahead offset in px
          initialScrollIndex={initialScrollIndex}
          extraData={{ highlightValue, value }}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          scrollEnabled={scrollEnabled}
          ListFooterComponent={listFooterComponent}
          ListFooterComponentStyle={listFooterComponentStyle}
        />
      )}
    </>
  )
}
